// The locale a voices.js identifier carries ("…ko-KR.Yuna" → "ko-KR"), and
// the voice it names ("Yuna").
const localeOf = (voiceURI) => {
  const m = /([a-z]{2,3})[-_]([A-Z]{2}|\d{3})/.exec(String(voiceURI || ''))
  return m ? `${m[1]}-${m[2]}` : ''
}
const nameOf = (voiceURI) => String(voiceURI || '').split('.').pop()
const normLang = (l) => String(l || '').replace('_', '-').toLowerCase()
// Accents aside, so "Mónica" (Chrome's name) matches "Monica" (Apple's id).
const fold = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// macOS novelty voices, as Chrome names them ("Rocko (Korean (South Korea))").
const NOVELTY = /^(Albert|Bad News|Bahh|Bells|Boing|Bubbles|Cellos|Eddy|Flo|Fred|Good News|Grandma|Grandpa|Jester|Junior|Kathy|Organ|Ralph|Reed|Rocko|Sandy|Shelley|Superstar|Trinoids|Whisper|Wobble|Zarvox)\b/

// How good a voice sounds, from what browsers report about it:
//   4  Apple Premium; Edge's Microsoft "Online (Natural)" neural voices
//   3  Apple Enhanced
//   2  the standard Apple voice, and anything else installed on the device
//   1  Google's online voices in Chrome (online only, and Chrome cuts them off
//      part-way through long passages), and old Microsoft desktop voices
//   0  novelty voices (Grandpa, Bubbles…) — used only if nothing else exists
export function voiceTier(v) {
  const uri = String(v.voiceURI || '')
  const name = String(v.name || '')
  if (/\.eloquence\.|\.speech\.synthesis\./.test(uri) || NOVELTY.test(name)) return 0
  if (/\.premium\./.test(uri) || /\(Premium\)/.test(name)) return 4
  if (/^Microsoft .*Online \(Natural\)/.test(name)) return 4
  if (/\.enhanced\./.test(uri) || /\(Enhanced\)/.test(name)) return 3
  if (/^Google /.test(name)) return 1
  if (/^Microsoft /.test(name)) return 1
  return 2
}

// The best of the voices a browser offers for the identifier's locale — those
// for exactly that locale if any exist (European Portuguese isn't Brazilian),
// else any of the same language — with the named voice winning a tie.
export function bestVoice(list, voiceURI) {
  const loc = localeOf(voiceURI).toLowerCase()
  if (!loc) return null
  const exact = list.filter((v) => normLang(v.lang) === loc)
  const pool = exact.length ? exact : list.filter((v) => normLang(v.lang).startsWith(loc.split('-')[0] + '-'))
  const wanted = fold(nameOf(voiceURI))
  const named = (v) => (fold(v.name).startsWith(wanted) ? 1 : 0)
  let best = null
  for (const v of pool) {
    if (!best) { best = v; continue }
    const d = voiceTier(v) - voiceTier(best)
    if (d > 0 || (d === 0 && named(v) > named(best))) best = v
  }
  return best
}

// Resolve the voice to use, and the language to stamp on the utterance: the
// best voice this browser has for the locale (see bestVoice), else no voice
// but the locale, so the engine picks its own — without that, Korean text went
// to the default English voice, which on iOS is silence. A value that is a
// bare voice name rather than an identifier is still honoured as that voice.
export function pickVoice(voiceURI) {
  const list = ('speechSynthesis' in window) ? speechSynthesis.getVoices() : []
  const loc = localeOf(voiceURI)
  if (!loc) {
    const byName = list.find((v) => v.voiceURI === voiceURI || v.name === voiceURI)
    return byName ? { voice: byName, lang: byName.lang } : { voice: null, lang: '' }
  }
  const voice = bestVoice(list, voiceURI)
  return { voice, lang: voice ? voice.lang : loc }
}

export function speak(text, voiceURI, voiceSpeed) {
  if (!('speechSynthesis' in window) || !text) return
  const { voice, lang } = pickVoice(voiceURI)
  // A bare voice name carries no locale, and Chrome's list loads late: when we
  // know neither voice nor language, wait once for the list. Identifiers carry
  // the locale, so they never wait — on iOS the list is often empty until
  // something has spoken, and waiting left taps silent.
  if (!voice && !lang && speechSynthesis.getVoices().length === 0) {
    speechSynthesis.addEventListener('voiceschanged', () => speak(text, voiceURI, voiceSpeed), { once: true })
    return
  }
  const utterance = new SpeechSynthesisUtterance(text)
  if (voice) utterance.voice = voice
  if (lang) utterance.lang = lang
  // Guard against an undefined/NaN speed: setting utterance.rate to a
  // non-number makes some browsers play back at a broken, very fast rate.
  utterance.rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
  speechSynthesis.speak(utterance)
}

// Stop whatever is currently being spoken.
export function stopSpeaking() {
  if ('speechSynthesis' in window) speechSynthesis.cancel()
}

// Keep a reference to the live utterance: Chrome garbage-collects utterances
// mid-speech otherwise, and their events silently stop firing.
let currentUtterance = null

// Speak with progress callbacks so a view can highlight the word being read.
//   onBoundary({ charIndex, charLength, estimated }) — per word where the
//   browser reports boundaries. Where it never does (Safari, many CJK voices)
//   a timer estimates the position from characters-per-second × rate, so the
//   highlight still moves along the text. `cps` tunes that estimate (Latin
//   script ≈ 13, Korean / Japanese / Chinese ≈ 4.5).
// Returns { cancel, promise } — the promise resolves when speech ends, which
// lets a "read all" chain sentences one after another.
export function speakTracked(text, voiceURI, voiceSpeed, { onStart, onBoundary, onEnd, cps } = {}) {
  let resolveDone
  const promise = new Promise((r) => { resolveDone = r })
  if (!('speechSynthesis' in window) || !text) {
    if (onEnd) onEnd()
    resolveDone()
    return { cancel() {}, promise }
  }
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
  let timer = null
  let gotBoundary = false
  let ended = false
  const finish = () => {
    if (ended) return
    ended = true
    if (timer) clearInterval(timer)
    if (onEnd) onEnd()
    resolveDone()
  }
  const start = () => {
    const utterance = new SpeechSynthesisUtterance(text)
    const { voice, lang } = pickVoice(voiceURI)
    if (voice) utterance.voice = voice
    if (lang) utterance.lang = lang
    utterance.rate = rate
    utterance.onstart = () => {
      if (onStart) onStart()
      const t0 = performance.now()
      const charsPerSec = (cps || 12) * rate
      timer = setInterval(() => {
        if (gotBoundary || ended) { clearInterval(timer); return }
        const idx = Math.min(text.length - 1, Math.floor(((performance.now() - t0) / 1000) * charsPerSec))
        if (onBoundary) onBoundary({ charIndex: idx, estimated: true })
      }, 90)
    }
    utterance.onboundary = (e) => {
      if (e.name && e.name !== 'word') return
      gotBoundary = true
      if (timer) clearInterval(timer)
      if (onBoundary) onBoundary({ charIndex: e.charIndex || 0, charLength: e.charLength, estimated: false })
    }
    utterance.onend = finish
    utterance.onerror = finish
    currentUtterance = utterance
    // One voice at a time. iOS Safari sometimes swallows an utterance queued in
    // the same tick as cancel(), so give it a beat when something was playing.
    if (speechSynthesis.speaking || speechSynthesis.pending) {
      speechSynthesis.cancel()
      setTimeout(() => speechSynthesis.speak(utterance), 60)
    } else {
      speechSynthesis.speak(utterance)
    }
  }
  // Start now when we know a voice or at least a language (identifiers carry
  // the locale); wait once for the list only when we know neither, which is a
  // bare voice name before Chrome's list has loaded (see speak()).
  const probe = pickVoice(voiceURI)
  if (!probe.voice && !probe.lang && speechSynthesis.getVoices().length === 0) {
    speechSynthesis.addEventListener('voiceschanged', start, { once: true })
  } else {
    start()
  }
  // Only cancel the engine if THIS utterance is still the live one — otherwise
  // an unmounting component would cut off whatever started speaking after it.
  return { cancel: () => { if (!ended) { speechSynthesis.cancel(); finish() } }, promise }
}
